// StreakLedger.jsx

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../../hooks/useFetch";
import StreakLedgerItem from "./StreakLedgerItem";

const MIN_TRANSFER_POINTS = 100;
const TRANSFER_DEDUCTION_PCT = 25;

const validateTUCode = (code) => /^TU[A-Z0-9]{12}$/.test(code);

const StreakLedger = ({ search, setSearch, records, selected, setSelected, onRefresh }) => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const token = authState?.token;
    const user  = authState?.user;

    const [refreshing, setRefreshing] = useState(false);

    // ── Transfer modal state ──────────────────────────────────────────────
    const [showTransfer, setShowTransfer] = useState(false);
    // const [step, setStep] = useState("verify"); // verify | details | confirm
    const [step, setStep] = useState("shop"); // shop | verify | details | confirm
    const [selectedShop, setSelectedShop] = useState(null);
    const [receiverCode, setReceiverCode] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState("");
    const [receiver, setReceiver] = useState(null); // { _id, name }
    const [points, setPoints] = useState("");
    const [remarks, setRemarks] = useState("");
    const [transferring, setTransferring] = useState(false);
    const [transferMsg, setTransferMsg] = useState(null);

    const totalBalance = records.reduce(
        (sum, r) => sum + (r.inOutType === "In" ? Number(r.points || 0) : -Number(r.points || 0)),
        0
    );

    const shopBalances = React.useMemo(() => {
        const map = {};
        records
            .filter((r) => r.streakModule === "FindItLocal" && r.shopkeeperCode)
            .forEach((r) => {
                const key = r.shopkeeperCode;
                if (!map[key]) {
                    map[key] = {
                        shopkeeperCode: r.shopkeeperCode,
                        shopkeeperName: r.shopkeeperName,
                        shopName: r.shopName,
                        merchantId: r.merchantId || null,
                        balance: 0,
                    };
                }
                map[key].balance += r.inOutType === "In" ? Number(r.points || 0) : -Number(r.points || 0);
            });
        return Object.values(map).filter((s) => s.balance > 0);
    }, [records]);

    const refreshLedger = async () => {
        try {
            setRefreshing(true);
            const res = await fetchData({
                url: "/pd/streaks/ledger",
                method: "post",
                data: { userId: user?._id },
                headers: { Authorization: token },
            });
            if (res?.success) {
                onRefresh?.(res.data);
            }
        } catch (err) {
            console.error("Refresh ledger error:", err);
        } finally {
            setRefreshing(false);
        }
    };

    const resetTransfer = () => {
        // setStep("verify");
        setStep("shop");
        setSelectedShop(null);
        setReceiverCode("");
        setVerifyError("");
        setReceiver(null);
        setPoints("");
        setRemarks("");
        setTransferMsg(null);
    };

    const closeTransfer = () => {
        setShowTransfer(false);
        resetTransfer();
    };

    const verifyReceiver = async () => {
        setVerifyError("");

        if (!validateTUCode(receiverCode)) {
            setVerifyError("Invalid TU Code — must be 14 characters starting with TU");
            return;
        }

        if (receiverCode === user?.think5whyscode) {
            setVerifyError("You cannot transfer points to yourself");
            return;
        }

        try {
            setVerifying(true);
            const res = await fetchData({
                url: "/auth/verifyreferralcode",
                method: "post",
                headers: { Authorization: token },
                data: { think5whyscode: receiverCode },
            });

            if (res?.status) {
                const t5w = res.t5wcode;
                setReceiver({
                    _id: t5w._id,
                    code: receiverCode,
                    name: t5w.name || "",
                });
                setStep("details");
            } else {
                setVerifyError("Receiver code not found. Please check and try again.");
            }
        } catch {
            setVerifyError("Verification failed. Please try again.");
        } finally {
            setVerifying(false);
        }
    };

    const pointsNum = Number(points || 0);
    const receiverWillGet = Math.floor(pointsNum * (1 - TRANSFER_DEDUCTION_PCT / 100));
    // const isValidAmount = pointsNum >= MIN_TRANSFER_POINTS && pointsNum <= totalBalance;
    const shopBalanceAvailable = selectedShop?.balance || 0;
    const isValidAmount = pointsNum >= MIN_TRANSFER_POINTS && pointsNum <= shopBalanceAvailable;

    const submitTransfer = async () => {
        if (!isValidAmount || !receiver) return;
        try {
            setTransferring(true);
            setTransferMsg(null);

            const res = await fetchData({
                url: "/pd/streaks/transfer-create",
                method: "post",
                data: {
                    senderId:   user?._id,
                    senderCode: user?.think5whyscode,
                    senderName: user?.name,
                    receiverId:   receiver._id,
                    receiverCode: receiver.code,
                    receiverName: receiver.name,
                                        merchantId:     selectedShop?.merchantId || null,
                    shopkeeperCode: selectedShop?.shopkeeperCode,
                    shopkeeperName: selectedShop?.shopkeeperName,
                    shopName:       selectedShop?.shopName,
                    points: pointsNum,
                    remarks,
                },
                headers: { Authorization: token },
            });

            if (res?.success) {
                setTransferMsg({ type: "success", text: res.message });
                refreshLedger();
                setTimeout(() => closeTransfer(), 2000);
            } else {
                setTransferMsg({ type: "error", text: res?.message || "Transfer failed" });
            }
        } catch (err) {
            setTransferMsg({ type: "error", text: err.message || "Transfer error" });
        } finally {
            setTransferring(false);
        }
    };

    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-cyan-200 mt-2">

            {/* Search + Refresh + Transfer */}
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-200 text-sm">🔍</span>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search ledger…"
                        className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder-gray-200 focus:outline-none focus:border-cyan-500/50 transition-colors"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch("")}
                            className="w-fit px-2 py-1 absolute right-3 top-1/2 -translate-y-1/2 text-gray-200 hover:text-gray-100 text-xs"
                        >
                            ✕
                        </button>
                    )}
                </div>
                <button
                    onClick={refreshLedger}
                    disabled={refreshing}
                    className="flex-shrink-0 text-[10px] w-fit px-2 py-1 rounded-xl bg-gray-800 border border-gray-700 text-gray-100 hover:border-cyan-500/50 transition-colors"
                >
                    {refreshing ? "..." : "🔄"}
                </button>
                <button
                    onClick={() => setShowTransfer(true)}
                    className="flex-shrink-0 text-[10px] w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-bold hover:opacity-90 transition-opacity"
                >
                    💸 Transfer
                </button>
            </div>

            {/* Balance summary */}
            {records.length > 0 && (
                <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-2">
                        <div className="font-black text-base text-green-400">
                            +{records.filter((r) => r.inOutType === "In").reduce((sum, r) => sum + Number(r.points || 0), 0)}
                        </div>
                        <div className="text-[10px] text-gray-100">Earned</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-2">
                        <div className="font-black text-base text-red-400">
                            -{records.filter((r) => r.inOutType === "Out").reduce((sum, r) => sum + Number(r.points || 0), 0)}
                        </div>
                        <div className="text-[10px] text-gray-100">Redeemed</div>
                    </div>
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-2">
                        <div className="font-black text-base text-cyan-400">{totalBalance}</div>
                        <div className="text-[10px] text-gray-100">Balance</div>
                    </div>
                </div>
            )}

            {records.length > 0 && (
                <div className="text-xs text-gray-100 text-center">
                    {records.length} entries
                </div>
            )}

            {/* List */}
            <div className="bg-gray-900/60 border border-gray-800 rounded-2xl overflow-hidden">
                {records.length === 0 ? (
                    <div className="p-8 text-center">
                        <div className="text-3xl mb-2">📒</div>
                        <div className="text-gray-200 text-sm">
                            {search ? "No matching entries" : "No ledger entries yet"}
                        </div>
                        {search && (
                            <button
                                onClick={() => setSearch("")}
                                className="w-fit px-2 py-1 mt-2 text-xs bg-cyan-700 text-white"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                ) : (
                    <AnimatePresence>
                        {records.map((item, idx) => (
                            <motion.div
                                key={item._id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: idx * 0.02 }}
                            >
                                <StreakLedgerItem
                                    item={item}
                                    selected={selected}
                                    setSelected={setSelected}
                                />
                            </motion.div>
                        ))}
                    </AnimatePresence>
                )}
            </div>

            {/* ── Transfer Modal ── */}
            <AnimatePresence>
                {showTransfer && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm p-3"
                        onClick={closeTransfer}
                    >
                        <motion.div
                            initial={{ y: 200 }}
                            animate={{ y: 0 }}
                            exit={{ y: 200 }}
                            transition={{ type: "spring", damping: 25 }}
                            className="w-full max-w-md max-h-[85vh] overflow-y-auto bg-gray-900 border border-purple-700/40 rounded-3xl p-4 space-y-4 mb-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="w-10 h-1 bg-gray-700 rounded-full mx-auto" />
                            <div className="text-purple-300 font-bold text-base">💸 Transfer Points</div>
{/* Step 0: Pick which merchant's points to transfer */}
                            {step === "shop" && (
                                <div className="space-y-2">
                                    <div className="text-xs text-gray-100 mb-1">
                                        Choose which merchant's balance to gift — the receiver's points stay tied to that same merchant.
                                    </div>
                                    {shopBalances.length === 0 ? (
                                        <div className="text-xs text-gray-400 text-center py-4">
                                            No transferable FindItLocal balances yet.
                                        </div>
                                    ) : (
                                        shopBalances.map((shop) => (
                                            <button
                                                key={shop.shopkeeperCode}
                                                onClick={() => { setSelectedShop(shop); setStep("verify"); }}
                                                className="w-full flex justify-between items-center bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 hover:border-purple-500/50 transition-colors"
                                            >
                                                <div className="text-left">
                                                    <div className="text-xs font-bold text-white">{shop.shopName || shop.shopkeeperName}</div>
                                                    <div className="text-[10px] text-gray-400">{shop.shopkeeperCode}</div>
                                                </div>
                                                <div className="text-green-400 font-black text-sm">{shop.balance} pts</div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                            {/* Step 1: Verify */}
                            {/* {step === "verify" && (
                                <div className="space-y-3">
                                    <div className="text-xs text-gray-100">
                                        Available balance: <span className="text-white font-bold">{totalBalance} pts</span>
                                    </div> */}
{step === "verify" && selectedShop && (
                                <div className="space-y-3">
                                    <div className="text-xs text-gray-100">
                                        🏪 {selectedShop.shopName || selectedShop.shopkeeperName}
                                        {" · "}Available: <span className="text-white font-bold">{selectedShop.balance} pts</span>
                                    </div>                                    
                                    <label className="text-[11px] text-gray-100 font-medium">
                                        Receiver's Think5Whys Code *
                                    </label>
                                    <input
                                        value={receiverCode}
                                        onChange={(e) => {
                                            setReceiverCode(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 14));
                                            setVerifyError("");
                                        }}
                                        placeholder="TU243BM5850150"
                                        maxLength={14}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/60 tracking-widest"
                                    />
                                    {verifyError && (
                                        <p className="text-red-400 text-[10px]">{verifyError}</p>
                                    )}
                                    <button
                                        onClick={verifyReceiver}
                                        disabled={verifying || !receiverCode}
                                        className="w-fit px-2 py-1 rounded-xl bg-purple-600 text-white text-sm font-bold disabled:opacity-50"
                                    >
                                        {verifying ? "Verifying..." : "Verify Receiver"}
                                    </button>
                                </div>
                            )}

                            {/* Step 2: Details */}
                            {step === "details" && receiver && (
                                <div className="space-y-3">
                                    <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-2 text-xs text-green-300">
                                        ✅ Verified — {receiver.name} ({receiver.code})
                                    </div>

                                    <div className="text-xs text-gray-100">
                                        Available: <span className="text-white font-bold">{totalBalance} pts</span>
                                        {" · "}Minimum transfer: <span className="text-white font-bold">{MIN_TRANSFER_POINTS} pts</span>
                                    </div>

                                    <label className="text-[11px] text-gray-100 font-medium">Points to transfer *</label>
                                    <input
                                        type="number"
                                        value={points}
                                        onChange={(e) => setPoints(e.target.value)}
                                        min={MIN_TRANSFER_POINTS}
                                        max={totalBalance}
                                        placeholder={`Min ${MIN_TRANSFER_POINTS}`}
                                        className="w-full bg-gray-800 border border-gray-600 rounded-xl px-4 py-3 text-white text-lg font-bold text-center"
                                    />

                                    {pointsNum > 0 && pointsNum < MIN_TRANSFER_POINTS && (
                                        <p className="text-red-400 text-[10px]">
                                            Minimum transfer amount is {MIN_TRANSFER_POINTS} points
                                        </p>
                                    )}
                                    {pointsNum > totalBalance && (
                                        <p className="text-red-400 text-[10px]">
                                            Cannot exceed available balance ({totalBalance} pts)
                                        </p>
                                    )}

                                    {isValidAmount && (
                                        <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-3 text-xs space-y-1">
                                            <div className="flex justify-between">
                                                <span className="text-gray-100">You send:</span>
                                                <span className="text-white font-bold">{pointsNum} pts</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-100">{receiver.name} receives ({TRANSFER_DEDUCTION_PCT}% transfer policy):</span>
                                                <span className="text-green-400 font-bold">{receiverWillGet} pts</span>
                                            </div>
                                        </div>
                                    )}

                                    <label className="text-[11px] text-gray-100 font-medium">Remarks (optional)</label>
                                    <input
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        placeholder="Reason for transfer…"
                                        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 resize-none"
                                    />

                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setStep("verify")}
                                            className="w-fit px-3 py-2 rounded-xl bg-gray-800 text-gray-100 text-sm font-bold"
                                        >
                                            Back
                                        </button>
                                        <button
                                            onClick={() => setStep("confirm")}
                                            disabled={!isValidAmount}
                                            className="w-fit px-2 py-1 rounded-xl bg-purple-600 text-white text-sm font-bold disabled:opacity-50"
                                        >
                                            Continue
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Confirm */}
                            {step === "confirm" && receiver && (
                                <div className="space-y-3">
                                    <div className="text-sm text-gray-100">
                                        Confirm transfer of <span className="text-white font-bold">{pointsNum} pts</span> to{" "}
                                        <span className="text-white font-bold">{receiver.name}</span> ({receiver.code})?
                                    </div>
                                    <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-3 text-xs space-y-1">
                                        <div className="flex justify-between">
                                            <span className="text-gray-100">Deducted from you:</span>
                                            <span className="text-red-400 font-bold">-{pointsNum} pts</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-100">Credited to receiver:</span>
                                            <span className="text-green-400 font-bold">+{receiverWillGet} pts</span>
                                        </div>
                                    </div>

                                    {transferMsg && (
                                        <div className={`p-2 rounded-xl text-xs font-bold text-center ${
                                            transferMsg.type === "success"
                                                ? "bg-green-900/40 text-green-300"
                                                : "bg-red-900/40 text-red-300"
                                        }`}>
                                            {transferMsg.text}
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setStep("details")}
                                            disabled={transferring}
                                            className="w-fit px-2 py-1 rounded-xl bg-gray-800 text-gray-100 text-sm font-bold disabled:opacity-50"
                                        >
                                            Back
                                        </button>
                                        <button
                                            onClick={submitTransfer}
                                            disabled={transferring}
                                            className="w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-bold disabled:opacity-50"
                                        >
                                            {transferring ? "Transferring..." : "Confirm Transfer"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={closeTransfer}
                                className="w-fit px-2 py-1 text-center text-xs text-gray-200 pt-1"
                            >
                                Cancel
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default StreakLedger;